import { useCallback } from 'react';
import { getPopularMovies, getPopularTVShows, getDetailedResultsBatch } from '../services/api';

const excludedGenreIds = [99, 10770]; // Exclude Documentaries and TV movies from randomizer

export function usePopular() {
  // Fisher-Yates shuffle
  const getRandomResults = useCallback((resultsArray, count) => {
    if (!resultsArray || resultsArray.length === 0) return [];
    
    const shuffled = [...resultsArray];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    return shuffled.slice(0, count);
  }, []);

  const filterExcludedGenres = useCallback((items) => {
    if (!items || !Array.isArray(items)) return [];
    return items.filter(item => 
      !item.genre_ids || !item.genre_ids.some(genreId => excludedGenreIds.includes(genreId))
    );
  }, []);

  const getDetailedResults = useCallback(async (items, mediaType, onProgress) => {
    if (!items || items.length === 0) return [];
    try {
      const results = await getDetailedResultsBatch(items, mediaType, 3);
      
      const validResults = (results || []).filter(item => {
        const isOnCrunchyroll = item.streamingProviders && 
                                item.streamingProviders.some(p => p.provider_name === 'Crunchyroll');
        
        const isAnime = item.genre_ids && 
                        item.genre_ids.includes(16) && 
                        item.original_language === 'ja';
                        
        return !isOnCrunchyroll && !isAnime;
      });

      const finalSelection = validResults.length > 0 ? validResults.slice(0, 6) : (results || items).slice(0, 6);
      if (onProgress) {
        onProgress(finalSelection);
      }
      return finalSelection;
    } catch (error) {
      console.error(`Error in getDetailedResults for ${mediaType}:`, error);
      return items.slice(0, 6);
    }
  }, []);

  const handlePopularMovies = useCallback(async (applyFilters, onInitialItems) => {
    try {
      const popularMovies = await getPopularMovies();
      
      if (!popularMovies || popularMovies.length === 0) {
        return [];
      }

      const filteredByGenre = filterExcludedGenres(popularMovies);
      const filteredMovies = applyFilters ? applyFilters(filteredByGenre) : filteredByGenre;
      
      const candidatePool = (filteredMovies && filteredMovies.length > 0) ? filteredMovies : popularMovies;

      const sampleSize = Math.min(10, candidatePool.length);
      const randomMovies = getRandomResults(candidatePool, sampleSize);

      // Instantly supply preliminary candidates for instant UI display
      const preliminary = randomMovies.slice(0, 6).map(item => ({
        ...item,
        mediaType: 'movie',
        tmdbURL: `https://www.themoviedb.org/movie/${item.id}`,
      }));

      if (onInitialItems && preliminary.length > 0) {
        onInitialItems(preliminary);
      }
      
      return await getDetailedResults(randomMovies, 'movie', onInitialItems);
    } catch (error) {
      console.error('Error in handlePopularMovies:', error);
      return [];
    }
  }, [getRandomResults, getDetailedResults, filterExcludedGenres]);

  const handlePopularTVShows = useCallback(async (applyFilters, onInitialItems) => {
    try {
      const popularTVShows = await getPopularTVShows();
      
      if (!popularTVShows || popularTVShows.length === 0) {
        return [];
      }

      const filteredTVShows = applyFilters ? applyFilters(popularTVShows) : popularTVShows;
      
      const candidatePool = (filteredTVShows && filteredTVShows.length > 0) ? filteredTVShows : popularTVShows;

      const sampleSize = Math.min(10, candidatePool.length);
      const randomTVShows = getRandomResults(candidatePool, sampleSize);

      // Instantly supply preliminary candidates for instant UI display
      const preliminary = randomTVShows.slice(0, 6).map(item => ({
        ...item,
        mediaType: 'tv',
        tmdbURL: `https://www.themoviedb.org/tv/${item.id}`,
      }));

      if (onInitialItems && preliminary.length > 0) {
        onInitialItems(preliminary);
      }
      
      return await getDetailedResults(randomTVShows, 'tv', onInitialItems);
    } catch (error) {
      console.error('Error in handlePopularTVShows:', error);
      return [];
    }
  }, [getRandomResults, getDetailedResults]);

  return {
    getRandomResults,
    handlePopularMovies,
    handlePopularTVShows,
    getDetailedResults,
  };
}